import React, { useMemo, useState } from "react";
import { useGSheet } from "@/hooks/useGSheet";
import {
  LiveHeader,
  StateBlock,
  Badge,
  crc,
  cell,
  fmtDate,
  phone,
} from "@/components/live/LiveShared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { TrendingDown, Loader2, History, Undo2 } from "lucide-react";

const PAYMENT_METHODS = [
  "Sinpe",
  "Efectivo",
  "Tarjeta",
  "Transferencia",
  "Otro",
];

export default function LiveCollectionsPage() {
  const { data, loading, error, refresh } = useGSheet("collections");
  const {
    data: payments,
    loading: paymentsLoading,
    error: paymentsError,
    refresh: refreshPayments,
  } = useGSheet("payments");

  const [paymentOrder, setPaymentOrder] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [confirmingPayment, setConfirmingPayment] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [historyOrder, setHistoryOrder] = useState(null);
  const [reversalPayment, setReversalPayment] = useState(null);
  const [reversalReason, setReversalReason] = useState("");
  const [reversing, setReversing] = useState(false);

  const totalDue = useMemo(
    () => data.reduce((s, r) => s + (Number(r.balance_due) || 0), 0),
    [data]
  );

  const reversedPaymentIds = useMemo(() => {
    const ids = new Set();
    payments.forEach((p) => {
      if (p.reversal_of !== "" && p.reversal_of != null) {
        ids.add(String(p.reversal_of));
      }
    });
    return ids;
  }, [payments]);

  const historyPayments = useMemo(() => {
    if (!historyOrder) return [];
    return payments
      .filter((p) => String(p.order_id) === String(historyOrder.order_id))
      .sort((a, b) => {
        const ad = a.last_payment_date ? new Date(a.last_payment_date).getTime() : 0;
        const bd = b.last_payment_date ? new Date(b.last_payment_date).getTime() : 0;
        return bd - ad;
      });
  }, [payments, historyOrder]);

  const openPayment = (row) => {
    setPaymentOrder(row);
    setPaymentAmount("");
    setPaymentMethod("");
    setConfirmingPayment(false);
  };

  const closePayment = () => {
    if (submitting) return;
    setPaymentOrder(null);
    setPaymentAmount("");
    setPaymentMethod("");
    setConfirmingPayment(false);
  };

  const requestPaymentConfirmation = () => {
    if (!paymentOrder) return;

    const amount = Number(paymentAmount);
    const balance = Number(paymentOrder.balance_due || 0);

    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error("Ingresa un monto válido mayor que cero.");
      return;
    }

    if (amount > balance) {
      toast.error(`El pago no puede exceder el saldo actual de ${crc(balance)}.`);
      return;
    }

    if (!paymentMethod) {
      toast.error("Selecciona un método de pago.");
      return;
    }

    setConfirmingPayment(true);
  };

  const submitPayment = async () => {
    if (!paymentOrder) return;

    const amount = Number(paymentAmount);
    setSubmitting(true);

    try {
      await api.post("/gsheet/register-payment", {
        order_id: String(paymentOrder.order_id),
        payment_amount: amount,
        payment_method: paymentMethod,
      });

      toast.success(
        `Pago registrado en orden #${paymentOrder.order_id}: ${crc(amount)}`
      );

      setPaymentOrder(null);
      setPaymentAmount("");
      setPaymentMethod("");
      setConfirmingPayment(false);

      await Promise.all([refresh(), refreshPayments()]);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "No se pudo registrar el pago."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const openHistory = (row) => {
    setHistoryOrder(row);
    setReversalPayment(null);
    setReversalReason("");
  };

  const closeHistory = () => {
    if (reversing) return;
    setHistoryOrder(null);
    setReversalPayment(null);
    setReversalReason("");
  };

  const openReversal = (payment) => {
    setReversalPayment(payment);
    setReversalReason("");
  };

  const submitReversal = async () => {
    if (!historyOrder || !reversalPayment) return;

    const reason = reversalReason.trim();
    if (!reason) {
      toast.error("El motivo de reversión es obligatorio.");
      return;
    }

    setReversing(true);

    try {
      const result = await api.post("/gsheet/reverse-payment", {
        order_id: String(historyOrder.order_id),
        payment_id: String(reversalPayment.payment_id),
        reason,
      });

      toast.success(
        `Pago ${reversalPayment.payment_id} revertido: ${crc(
          Math.abs(Number(reversalPayment.payment_amount) || 0)
        )}`
      );

      setReversalPayment(null);
      setReversalReason("");

      await Promise.all([refresh(), refreshPayments()]);

      if (result?.data?.newBalance != null) {
        setHistoryOrder((current) =>
          current
            ? { ...current, balance_due: result.data.newBalance }
            : current
        );
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "No se pudo revertir el pago."
      );
    } finally {
      setReversing(false);
    }
  };

  const isReversiblePayment = (p) => {
    const amount = Number(p.payment_amount) || 0;
    const type = String(p.transaction_type || "").toUpperCase();
    return (
      amount > 0 &&
      type !== "REVERSAL" &&
      !reversedPaymentIds.has(String(p.payment_id))
    );
  };

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6">
      <LiveHeader
        title="Cobros / Collections (en vivo)"
        subtitle="Cuentas por cobrar desde Google Sheets"
        count={data.length}
        loading={loading}
        onRefresh={() => Promise.all([refresh(), refreshPayments()])}
        testid="live-collections"
      />

      {!loading && !error && data.length > 0 && (
        <div className="mb-5 inline-flex items-center gap-3 rounded-2xl border border-border bg-card px-5 py-4 shadow-sm">
          <TrendingDown className="h-5 w-5 text-rose-500" />
          <div>
            <p className="text-xs text-muted-foreground">
              Saldo pendiente total
            </p>
            <p
              className="font-mono font-bold text-2xl text-foreground"
              data-testid="collections-total-due"
            >
              {crc(totalDue)}
            </p>
          </div>
        </div>
      )}

      <StateBlock
        loading={loading}
        error={error}
        empty={!loading && !error && data.length === 0}
        onRetry={refresh}
      >
        <div className="overflow-x-auto rounded-2xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60 text-left">
              <tr className="text-xs uppercase text-muted-foreground">
                <th className="px-4 py-3">Orden</th>
                <th className="px-4 py-3">Cliente</th>
                <th className="px-4 py-3">Canal</th>
                <th className="px-4 py-3 text-right">Total</th>
                <th className="px-4 py-3 text-right">Pagado</th>
                <th className="px-4 py-3 text-right">Saldo</th>
                <th className="px-4 py-3 text-center">Días</th>
                <th className="px-4 py-3">Prioridad</th>
                <th className="px-4 py-3">Pago</th>
                <th className="px-4 py-3">Próxima acción</th>
                <th className="px-4 py-3 text-right">Acción</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-border">
              {data.map((r, i) => (
                <tr
                  key={`${r.order_id}-${i}`}
                  className="hover:bg-secondary/30"
                  data-testid={`collection-row-${r.order_id}`}
                >
                  <td className="px-4 py-3 font-mono font-semibold text-foreground">
                    {cell(r.order_id)}
                  </td>

                  <td className="px-4 py-3 font-medium text-foreground">
                    {cell(r.customer_name)}
                    <span className="block text-xs text-muted-foreground">
                      {phone(r.customer_phone)}
                    </span>
                  </td>

                  <td className="px-4 py-3">
                    {cell(r.source_channel)}
                  </td>

                  <td className="px-4 py-3 text-right font-mono">
                    {crc(r.total_amount)}
                  </td>

                  <td className="px-4 py-3 text-right font-mono">
                    {crc(r.paid_amount)}
                  </td>

                  <td className="px-4 py-3 text-right font-mono font-semibold text-rose-600 dark:text-rose-400">
                    {crc(r.balance_due)}
                  </td>

                  <td className="px-4 py-3 text-center font-mono">
                    {cell(r.pending_days)}
                  </td>

                  <td className="px-4 py-3">
                    <Badge value={r.priority} />
                  </td>

                  <td className="px-4 py-3">
                    <Badge value={r.payment_status} />
                  </td>

                  <td className="px-4 py-3 text-muted-foreground">
                    {cell(r.collection_next_action)}
                    {r.last_payment_date ? (
                      <span className="block text-xs">
                        últ. pago: {fmtDate(r.last_payment_date)}
                      </span>
                    ) : null}
                  </td>

                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openHistory(r)}
                        data-testid={`payment-history-button-${r.order_id}`}
                      >
                        <History className="mr-1 h-4 w-4" />
                        Pagos
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openPayment(r)}
                        disabled={Number(r.balance_due || 0) <= 0}
                        data-testid={`register-payment-button-${r.order_id}`}
                      >
                        Pago
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </StateBlock>

      <Dialog
        open={Boolean(paymentOrder)}
        onOpenChange={(open) => {
          if (!open) closePayment();
        }}
      >
        <DialogContent
          data-testid={
            paymentOrder
              ? `register-payment-dialog-${paymentOrder.order_id}`
              : "register-payment-dialog"
          }
        >
          <DialogHeader>
            <DialogTitle>
              {confirmingPayment ? "Confirmar pago" : "Registrar pago"}
            </DialogTitle>
            <DialogDescription>
              {paymentOrder
                ? `Orden #${paymentOrder.order_id} · Saldo actual ${crc(
                    paymentOrder.balance_due
                  )}`
                : ""}
            </DialogDescription>
          </DialogHeader>

          {!confirmingPayment ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="payment-amount">Monto</Label>
                <Input
                  id="payment-amount"
                  type="number"
                  min="0"
                  step="1"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  placeholder="0"
                  disabled={submitting}
                  data-testid="register-payment-amount"
                />
              </div>

              <div className="space-y-2">
                <Label>Método de pago</Label>
                <Select
                  value={paymentMethod}
                  onValueChange={setPaymentMethod}
                  disabled={submitting}
                >
                  <SelectTrigger data-testid="register-payment-method">
                    <SelectValue placeholder="Seleccionar método" />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map((method) => (
                      <SelectItem key={method} value={method}>
                        {method}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ) : (
            <div className="rounded-lg border p-4 text-sm space-y-2">
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">Orden</span>
                <span className="font-mono">#{paymentOrder?.order_id}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">Monto</span>
                <span className="font-mono font-semibold">{crc(paymentAmount)}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">Método</span>
                <span>{paymentMethod}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">Saldo después</span>
                <span className="font-mono">
                  {crc(
                    Math.max(
                      Number(paymentOrder?.balance_due || 0) - Number(paymentAmount || 0),
                      0
                    )
                  )}
                </span>
              </div>
            </div>
          )}

          <DialogFooter>
            {!confirmingPayment ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={closePayment}
                  disabled={submitting}
                >
                  Cancelar
                </Button>
                <Button
                  type="button"
                  onClick={requestPaymentConfirmation}
                  disabled={submitting}
                  data-testid="register-payment-review"
                >
                  Revisar pago
                </Button>
              </>
            ) : (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setConfirmingPayment(false)}
                  disabled={submitting}
                >
                  Atrás
                </Button>
                <Button
                  type="button"
                  onClick={submitPayment}
                  disabled={submitting}
                  data-testid="register-payment-submit"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Registrando...
                    </>
                  ) : (
                    "Confirmar pago"
                  )}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(historyOrder)}
        onOpenChange={(open) => {
          if (!open) closeHistory();
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Historial de pagos</DialogTitle>
            <DialogDescription>
              {historyOrder ? `Orden #${historyOrder.order_id}` : ""}
            </DialogDescription>
          </DialogHeader>

          {paymentsLoading ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              Cargando pagos...
            </div>
          ) : paymentsError ? (
            <div className="py-8 text-center text-sm text-destructive">
              No se pudo cargar el historial de pagos.
            </div>
          ) : historyPayments.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              No hay movimientos registrados para esta orden.
            </div>
          ) : (
            <div className="max-h-[360px] overflow-y-auto rounded-lg border divide-y">
              {historyPayments.map((p, idx) => {
                const amount = Number(p.payment_amount) || 0;
                const isReversal =
                  amount < 0 || String(p.transaction_type || "").toUpperCase() === "REVERSAL";
                const reversed = reversedPaymentIds.has(String(p.payment_id));

                return (
                  <div
                    key={`${p.payment_id}-${idx}`}
                    className="flex items-center justify-between gap-4 p-3"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-semibold">
                          {cell(p.payment_id)}
                        </span>
                        <span className="text-sm">{crc(amount)}</span>
                        {isReversal ? <Badge value="Reverso" /> : null}
                        {reversed ? <Badge value="Revertido" /> : null}
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        {cell(p.payment_method)}
                        {p.last_payment_date ? ` · ${fmtDate(p.last_payment_date)}` : ""}
                      </div>
                      {p.reversal_reason ? (
                        <div className="mt-1 text-xs text-muted-foreground">
                          Motivo: {p.reversal_reason}
                        </div>
                      ) : null}
                    </div>

                    {isReversiblePayment(p) ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openReversal(p)}
                        data-testid={`reverse-payment-button-${p.payment_id}`}
                      >
                        <Undo2 className="mr-1 h-4 w-4" />
                        Anular
                      </Button>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}

          {reversalPayment ? (
            <div className="space-y-4 rounded-lg border p-4">
              <div>
                <p className="font-medium">Confirmar reversión</p>
                <p className="text-sm text-muted-foreground">
                  Pago {reversalPayment.payment_id} · {crc(reversalPayment.payment_amount)} · {cell(reversalPayment.payment_method)}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reversal-reason">Motivo obligatorio</Label>
                <Input
                  id="reversal-reason"
                  value={reversalReason}
                  onChange={(e) => setReversalReason(e.target.value)}
                  placeholder="Ej. monto registrado incorrectamente"
                  disabled={reversing}
                  data-testid="reverse-payment-reason"
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setReversalPayment(null);
                    setReversalReason("");
                  }}
                  disabled={reversing}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={submitReversal}
                  disabled={reversing}
                  data-testid="reverse-payment-confirm"
                >
                  {reversing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Revirtiendo...
                    </>
                  ) : (
                    "Confirmar reversión"
                  )}
                </Button>
              </div>
            </div>
          ) : null}

          {!reversalPayment ? (
            <DialogFooter>
              <Button variant="outline" onClick={closeHistory}>
                Cerrar
              </Button>
            </DialogFooter>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
